import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  recentPostsOf,
  resolveDid,
  runActionsParallel,
  type ActionKind,
  type ActionTarget,
  type ActorSession,
} from '../utils/ownActions'
import { api } from '../api/client'
import { useAuth } from '../store/auth'
import { useSettings } from '../store/settings'
import { normalizeHandle } from '../utils/handle'

type AccountProgress = {
  done: number
  total: number
  ok: number
  err: number
  lastError: string | null
}

const blankProgress = (): AccountProgress => ({
  done: 0,
  total: 0,
  ok: 0,
  err: 0,
  lastError: null,
})

export function Lurker() {
  const navigate = useNavigate()
  const session = useAuth((s) => s.session)
  const service = useSettings((s) => s.service)

  const [target, setTarget] = useState('')
  const [action, setAction] = useState<ActionKind>('like')
  const [count, setCount] = useState(10)
  // Random-spacing range. Min and max are both in ms; each between-write
  // pause picks a uniform random ms in [min, max].
  const [minSpacingMs, setMinSpacingMs] = useState(2000)
  const [maxSpacingMs, setMaxSpacingMs] = useState(8000)

  // Extra accounts the user has attached via app-password login. The auth
  // session is implicit (always included if present); these are added on
  // top so several accounts can act in parallel.
  const [extraSessions, setExtraSessions] = useState<ActorSession[]>([])
  const [addHandle, setAddHandle] = useState('')
  const [addPassword, setAddPassword] = useState('')
  const [adding, setAdding] = useState(false)
  const [addError, setAddError] = useState<string | null>(null)

  const [running, setRunning] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [previewUris, setPreviewUris] = useState<string[]>([])
  const [perAccount, setPerAccount] = useState<Record<string, AccountProgress>>({})
  const ac = useState(() => ({ controller: null as AbortController | null }))[0]

  // Combined session list: signed-in + extras.
  const allSessions: ActorSession[] = session
    ? [{ ...session, handle: session.handle }, ...extraSessions]
    : extraSessions

  if (!session && extraSessions.length === 0) {
    return (
      <main className="mx-auto mt-10 max-w-xl px-4">
        <div className="glass p-5 text-center">
          <p className="mb-3 text-sm">
            Sign in (or attach an app-password account below) to use this tool. Acts ONLY as
            sessions you've authenticated with.
          </p>
          <button className="glass-button" onClick={() => navigate('/')}>
            home
          </button>
        </div>
      </main>
    )
  }

  async function addAccount() {
    setAddError(null)
    const handle = normalizeHandle(addHandle, service)
    if (!handle || !addPassword) {
      setAddError('handle + app password required')
      return
    }
    if (allSessions.some((s) => s.handle.toLowerCase() === handle.toLowerCase())) {
      setAddError('that account is already attached')
      return
    }
    setAdding(true)
    try {
      const fresh = await api.login({ service, identifier: handle, password: addPassword })
      setExtraSessions((prev) => [
        ...prev,
        {
          service: fresh.service,
          did: fresh.did,
          handle: fresh.handle,
          accessJwt: fresh.accessJwt,
          refreshJwt: fresh.refreshJwt,
        },
      ])
      setAddHandle('')
      setAddPassword('')
    } catch (e) {
      setAddError(e instanceof Error ? e.message : String(e))
    } finally {
      setAdding(false)
    }
  }

  function removeAccount(did: string) {
    setExtraSessions((prev) => prev.filter((s) => s.did !== did))
    setPerAccount((p) => {
      const { [did]: _drop, ...rest } = p
      void _drop
      return rest
    })
  }

  async function preview() {
    setError(null)
    setPreviewUris([])
    const handle = normalizeHandle(target, service)
    if (!handle) {
      setError('handle required')
      return
    }
    if (action === 'follow') {
      const did = await resolveDid(handle, service)
      if (!did) {
        setError('could not resolve handle')
        return
      }
      setPreviewUris([did])
      return
    }
    const posts = await recentPostsOf(handle, service, count, session?.accessJwt)
    setPreviewUris(posts.map((p) => p.uri))
  }

  async function run() {
    if (allSessions.length === 0) return
    setError(null)
    setRunning(true)
    setPerAccount(
      Object.fromEntries(allSessions.map((s) => [s.did, blankProgress()])),
    )
    const handle = normalizeHandle(target, service)
    if (!handle) {
      setError('handle required')
      setRunning(false)
      return
    }
    const ctrl = new AbortController()
    ac.controller = ctrl
    try {
      let targets: ActionTarget[]
      if (action === 'follow') {
        const did = await resolveDid(handle, service)
        if (!did) throw new Error('could not resolve handle')
        targets = [{ kind: 'profile', did }]
      } else {
        const posts = await recentPostsOf(handle, service, count, session?.accessJwt)
        targets = posts.map((p) => ({ kind: 'post', uri: p.uri, cid: p.cid }))
      }
      // Initialise totals per account.
      setPerAccount((prev) => {
        const next = { ...prev }
        for (const s of allSessions) next[s.did] = { ...blankProgress(), total: targets.length }
        return next
      })
      await runActionsParallel({
        sessions: allSessions,
        action,
        targets,
        spacingMs:
          minSpacingMs === maxSpacingMs
            ? minSpacingMs
            : { min: Math.min(minSpacingMs, maxSpacingMs), max: Math.max(minSpacingMs, maxSpacingMs) },
        signal: ctrl.signal,
        onProgress: (did, done, total, err) =>
          setPerAccount((p) => {
            const cur = p[did] ?? blankProgress()
            return {
              ...p,
              [did]: {
                done,
                total,
                ok: cur.ok + (err ? 0 : 1),
                err: cur.err + (err ? 1 : 0),
                lastError: err,
              },
            }
          }),
      })
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setRunning(false)
      ac.controller = null
    }
  }

  function cancel() {
    ac.controller?.abort()
  }

  const totals = Object.values(perAccount).reduce(
    (acc, p) => ({
      done: acc.done + p.done,
      total: acc.total + p.total,
      ok: acc.ok + p.ok,
      err: acc.err + p.err,
    }),
    { done: 0, total: 0, ok: 0, err: 0 },
  )

  return (
    <main className="mx-auto mt-4 max-w-3xl px-4 pb-16">
      <div className="glass p-5">
        <div className="mb-2 flex items-baseline justify-between">
          <h1 className="text-xl font-extrabold tracking-tight">Lurker (multi-account)</h1>
          <button
            type="button"
            className="glass-button text-xs"
            onClick={() => navigate('/')}
            title="close"
          >
            ✕ close
          </button>
        </div>
        <p className="mb-4 text-sm text-ink-200">
          Acts as <strong>{allSessions.length}</strong>{' '}
          attached account{allSessions.length === 1 ? '' : 's'}. Each session runs the same target
          list in parallel with its own random-spaced schedule. Use ONLY accounts you own /
          control — this tool exists for testing detection heuristics, not for hammering live
          users.
        </p>

        {/* Attached-accounts panel */}
        <section className="mb-4 rounded-2xl border border-white/10 bg-white/5 p-3">
          <div className="mb-2 text-[11px] uppercase tracking-widest text-ink-300">
            accounts ({allSessions.length})
          </div>
          <ul className="mb-2 flex flex-wrap gap-1.5 text-xs">
            {session ? (
              <li className="flex items-center gap-1 rounded-full border border-emerald-400/40 bg-emerald-500/15 px-2 py-0.5 text-emerald-100">
                <span>★ @{session.handle}</span>
                <span className="text-[9px] text-emerald-200/70">(signed in)</span>
              </li>
            ) : null}
            {extraSessions.map((s) => (
              <li
                key={s.did}
                className="flex items-center gap-1 rounded-full border border-white/15 bg-white/10 px-2 py-0.5 text-ink-100"
              >
                <span>@{s.handle}</span>
                <button
                  type="button"
                  onClick={() => removeAccount(s.did)}
                  aria-label={`remove ${s.handle}`}
                  className="rounded-full px-1 text-[10px] text-ink-300 hover:bg-white/10 hover:text-white"
                  title="detach this account"
                >
                  ✕
                </button>
              </li>
            ))}
          </ul>
          <div className="grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
            <input
              className="glass-input text-xs"
              placeholder="handle.bsky.social"
              value={addHandle}
              onChange={(e) => setAddHandle(e.target.value)}
              spellCheck={false}
              autoCapitalize="off"
              autoCorrect="off"
            />
            <input
              className="glass-input text-xs"
              placeholder="app password (xxxx-xxxx-xxxx-xxxx)"
              type="password"
              value={addPassword}
              onChange={(e) => setAddPassword(e.target.value)}
              spellCheck={false}
              autoCapitalize="off"
              autoCorrect="off"
            />
            <button
              type="button"
              className="glass-button text-xs"
              onClick={addAccount}
              disabled={adding || !addHandle || !addPassword}
            >
              {adding ? 'adding…' : '+ attach'}
            </button>
          </div>
          {addError ? (
            <div className="mt-2 rounded-xl border border-rose-400/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-100">
              {addError}
            </div>
          ) : null}
          <p className="mt-2 text-[10px] text-ink-400">
            Use a Bluesky <strong>app password</strong> (Settings → App Passwords). Sessions live
            in this tab only — closing the page drops them.
          </p>
        </section>

        <div className="grid gap-3 sm:grid-cols-2">
          <label className="flex flex-col gap-1 text-xs uppercase tracking-widest text-ink-300">
            target handle
            <input
              className="glass-input"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              spellCheck={false}
              autoCapitalize="off"
              autoCorrect="off"
              placeholder="them.bsky.social"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs uppercase tracking-widest text-ink-300">
            action
            <select
              className="glass-input"
              value={action}
              onChange={(e) => setAction(e.target.value as ActionKind)}
            >
              <option value="like">like every recent post</option>
              <option value="repost">repost every recent post</option>
              <option value="follow">follow once</option>
            </select>
          </label>
          {action !== 'follow' ? (
            <label className="flex flex-col gap-1 text-xs uppercase tracking-widest text-ink-300">
              # of recent posts
              <input
                type="number"
                min={1}
                max={500}
                className="glass-input"
                value={count}
                onChange={(e) => setCount(Math.max(1, Math.min(500, Number(e.target.value) || 1)))}
              />
            </label>
          ) : null}
          <label className="flex flex-col gap-1 text-xs uppercase tracking-widest text-ink-300">
            min spacing (ms)
            <input
              type="number"
              min={500}
              step={100}
              className="glass-input"
              value={minSpacingMs}
              onChange={(e) =>
                setMinSpacingMs(Math.max(500, Math.min(60_000, Number(e.target.value) || 2000)))
              }
            />
          </label>
          <label className="flex flex-col gap-1 text-xs uppercase tracking-widest text-ink-300">
            max spacing (ms)
            <input
              type="number"
              min={500}
              step={100}
              className="glass-input"
              value={maxSpacingMs}
              onChange={(e) =>
                setMaxSpacingMs(Math.max(500, Math.min(60_000, Number(e.target.value) || 8000)))
              }
            />
          </label>
        </div>
        <p className="mt-1 text-[10px] text-ink-400">
          Each pause picks a uniform-random ms in [min, max]. Set min == max for a fixed cadence.
          Per-account schedules are independent so their writes don't fire in lockstep.
        </p>

        <div className="mt-3 flex flex-wrap gap-2">
          <button type="button" className="glass-button" onClick={preview} disabled={running}>
            preview
          </button>
          <button
            type="button"
            className="glass-button glass-button--accent"
            onClick={run}
            disabled={running || !target || allSessions.length === 0}
          >
            {running
              ? 'running…'
              : `run (${allSessions.length} account${allSessions.length === 1 ? '' : 's'})`}
          </button>
          {running ? (
            <button type="button" className="glass-button text-xs" onClick={cancel}>
              cancel
            </button>
          ) : null}
        </div>

        {error ? (
          <div className="mt-3 rounded-xl border border-rose-400/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-100">
            {error}
          </div>
        ) : null}

        {totals.total > 0 ? (
          <div className="mt-3 space-y-2">
            <div className="glass-tinted px-3 py-2 text-xs">
              <strong className="text-white">total</strong> {totals.done.toLocaleString()} /{' '}
              {totals.total.toLocaleString()} • ok {totals.ok.toLocaleString()} • err{' '}
              {totals.err.toLocaleString()}
            </div>
            <div className="space-y-1.5">
              {allSessions.map((s) => {
                const p = perAccount[s.did] ?? blankProgress()
                const pct =
                  p.total > 0 ? Math.min(100, Math.round((p.done / p.total) * 100)) : 0
                return (
                  <div
                    key={s.did}
                    className="rounded-xl border border-white/10 bg-black/30 px-3 py-2 text-[11px]"
                  >
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="font-semibold accent-text">@{s.handle}</span>
                      <span className="font-mono text-ink-300">
                        {p.done}/{p.total} • ok {p.ok} • err {p.err} • {pct}%
                      </span>
                    </div>
                    <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-white/5">
                      <div
                        className="h-1 rounded-full transition-all"
                        style={{
                          width: `${pct}%`,
                          background: p.err > 0 ? 'rgb(244, 63, 94)' : 'var(--accent-strong)',
                        }}
                      />
                    </div>
                    {p.lastError ? (
                      <div
                        className="mt-1 truncate text-rose-200"
                        title={p.lastError}
                      >
                        last error: {p.lastError}
                      </div>
                    ) : null}
                  </div>
                )
              })}
            </div>
          </div>
        ) : null}

        {previewUris.length > 0 ? (
          <div className="glass mt-3 max-h-64 overflow-y-auto p-2 text-xs">
            <div className="mb-1 text-ink-300">
              {previewUris.length} target{previewUris.length === 1 ? '' : 's'}:
            </div>
            <ul className="font-mono text-[10px] text-ink-200">
              {previewUris.map((u) => (
                <li key={u} className="truncate" title={u}>
                  {u}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </main>
  )
}
